import React, { useCallback, useEffect } from 'react'
import { useTranslation } from '@/localization/useTranslation'
import { useSearch, type SearchResults } from './hooks/useSearch'
import { useDisableBodyScroll } from '@/hooks/utils/useDisableBodyScroll'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClose } from '@fortawesome/free-solid-svg-icons'
import { useDebouncedState } from '@/hooks/utils/useDebouncedState'
import { Group } from '@/components/Group'
import { SimpleTabs } from '@/components/Tabs'
import { IQueryParams } from '@/types'
import { ContentGrid } from '@/components/ContentGrid'
import { CARD_TYPE } from '@/constants'
import { ISearchActorProps } from './types'

import classes from './styles.module.scss'

export const SearchActor: React.FC<ISearchActorProps> = (props) => {
  const { searchTerm = '', clearSearch, searchProps } = props
  const { params = {} as IQueryParams } = searchProps || {}
  const [term, setDebouncedValue] = useDebouncedState(searchTerm, 500)
  const { content, teams } = useSearch({ term, params, options: { placeholderItems: 20 } })
  const { t } = useTranslation()
  useDisableBodyScroll({ enabled: !!searchTerm, shouldPositionFixed: true })

  useEffect(() => {
    setDebouncedValue(searchTerm)
  }, [searchTerm, setDebouncedValue])

  if (!searchTerm) return null

  return (
    <div className={classes.container}>
      <Group className={classes.header} justify='between' align='center' mb='sm'>
        <div className={classes.title}>
          {t('Searching')}: {searchTerm}
        </div>
        <div className={classes.close} onClick={clearSearch}>
          <FontAwesomeIcon icon={faClose} />
        </div>
      </Group>
      <SearchResults
        content={content}
        teams={teams}
      />
    </div>
  )
}

const SearchResults: React.FC<SearchResults> = (props) => {
  const { content, teams } = props
  const { t } = useTranslation()
  const contentItems = content?.items || []
  const hasContent = contentItems.length > 0
  const teamItems = teams?.items || []
  const hasTeams = teamItems.length > 0

  const fetchNextContentPage = useCallback(() => {
    content.fetchNextPage()
  }, [content])

  const fetchNextTeamsPage = useCallback(() => {
    teams.fetchNextPage()
  }, [teams])

  return (
    <div className={classes.results}>
      <SimpleTabs
        showTabs={hasContent && hasTeams && !content?.isPlaceholderData && !teams?.isPlaceholderData}
      >
        {hasContent && <SimpleTabs.Tab label={t('Content')} tabId='content' />}
        {hasTeams && <SimpleTabs.Tab label={t('Teams')} tabId='teams' />}
        {hasContent &&
          <SimpleTabs.Content tabId='content'>
            <ContentGrid
              hasNextPage={content?.hasNextPage}
              isLoading={content?.isLoading}
              isFetching={content?.isFetching}
              isPlaceholderData={content?.isPlaceholderData}
              items={contentItems}
              handleNextPage={fetchNextContentPage}
            />
          </SimpleTabs.Content>
        }
        {hasTeams &&
          <SimpleTabs.Content tabId='teams'>
            <ContentGrid
              hasNextPage={teams?.hasNextPage}
              isLoading={teams?.isLoading}
              isFetching={teams?.isFetching}
              isPlaceholderData={teams?.isPlaceholderData}
              cardOptions={{ cardType: CARD_TYPE.TEAM }}
              items={teamItems}
              handleNextPage={fetchNextTeamsPage}
              columnWidth='150px'
            />
          </SimpleTabs.Content>
        }
      </SimpleTabs>
    </div>
  )
}